import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import BookingService from "../../services/booking.service";

/* -------- BUY SUBSCRIPTION -------- */
export const buySubscriptions = createAsyncThunk(
  "booking/buySubscriptions",
  async (payload, thunkAPI) => {
    try {
      const response = await BookingService.buySubscriptionsPlan(payload);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message ||
        "Buy subscription plan failed"
      );
    }
  }
);

/* -------- ACTIVATE FREE SUBSCRIPTION -------- */
export const activateFreeSubscription = createAsyncThunk(
  "booking/activateFreeSubscription",
  async (payload, thunkAPI) => {
    try {
      const response =
        await BookingService.activateFreeSubscription(payload);

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message ||
        "Failed to activate free subscription"
      );
    }
  }
);

/* -------- VERIFY PAYMENT -------- */
export const verifyPayment = createAsyncThunk(
  "booking/verifyPayment",
  async (payload, thunkAPI) => {
    try {
      const response = await BookingService.verifyPayment(payload);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message ||
        "Payment verification failed"
      );
    }
  }
);

/* -------- GET PURCHASE SUBSCRIPTIONS -------- */
export const getPurchaseSubscription = createAsyncThunk(
  "booking/getPurchaseSubscription",
  async (payload, thunkAPI) => {
    try {
      const response = await BookingService.getBooking(payload);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message ||
        "Failed to fetch subscriptions"
      );
    }
  }
);

const bookingSlice = createSlice({
  name: "booking",

  initialState: {
    /* Subscription history */
    data: [],
    page: 1,
    limit: 10,
    total: 0,

    /* Common states */
    isLoading: false,
    error: null,

    /* Single booking / payment */
    booking: null,
    paymentVerified: false,

    /* Free subscription */
    freeSubscription: null,
    freeSubscriptionActivated: false,
  },

  reducers: {
    clearBooking: (state) => {
      state.booking = null;
      state.paymentVerified = false;
      state.freeSubscription = null;
      state.freeSubscriptionActivated = false;
      state.error = null;
    },

    resetFreeSubscriptionStatus: (state) => {
      state.freeSubscription = null;
      state.freeSubscriptionActivated = false;
    },
  },

  extraReducers: (builder) => {
    builder

      /* =========================================
         BUY SUBSCRIPTION
      ========================================= */
      .addCase(buySubscriptions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })

      .addCase(buySubscriptions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.booking = action.payload;
      })

      .addCase(buySubscriptions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      /* =========================================
         ACTIVATE FREE SUBSCRIPTION
      ========================================= */
      .addCase(activateFreeSubscription.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.freeSubscriptionActivated = false;
      })

      .addCase(activateFreeSubscription.fulfilled, (state, action) => {
        state.isLoading = false;

        state.freeSubscription = action.payload?.data || null;
        state.freeSubscriptionActivated = true;

        // Also store it as the current booking/subscription
        state.booking = action.payload;
      })

      .addCase(activateFreeSubscription.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.freeSubscriptionActivated = false;
      })

      /* =========================================
         VERIFY PAYMENT
      ========================================= */
      .addCase(verifyPayment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })

      .addCase(verifyPayment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.paymentVerified = true;
        state.booking = action.payload;
      })

      .addCase(verifyPayment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.paymentVerified = false;
      })

      /* =========================================
         GET PURCHASE SUBSCRIPTION HISTORY
      ========================================= */
      .addCase(getPurchaseSubscription.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })

      .addCase(getPurchaseSubscription.fulfilled, (state, action) => {
        state.isLoading = false;

        state.data = action.payload?.data || [];
        state.page = action.payload?.page || 1;
        state.limit = action.payload?.limit || 10;
        state.total = action.payload?.total || 0;
      })

      .addCase(getPurchaseSubscription.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearBooking,
  resetFreeSubscriptionStatus,
} = bookingSlice.actions;

export default bookingSlice.reducer;